import {
  CONTROL_LOAD_ERROR_MSG,
  CONTROL_SEARCH_PLACEHOLDER,
} from "../../constants/ui";
import React, { useEffect, useState } from "react";
import {
  loadConversations,
  selectControlError,
  selectConversations,
  selectCurrConversation,
  selectLoading,
  setCurrConversation,
} from "@/features/control/controlSlice";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";

import { Conversation } from "../../types/conversation";
import { Input } from "../ui/input";
import NewConversationDialog from "./NewConversationDialog.tsx";
import { PencilLineIcon } from "lucide-react";
import clsx from "clsx";
import { elapsedTime } from "../../utils/time";
import { processFullname } from "@/utils/utils";

const ConversationList = () => {
  const dispatch = useAppDispatch();
  const conversations: Conversation[] = useAppSelector(selectConversations);
  const currConversationIndex = useAppSelector(selectCurrConversation);
  const loading = useAppSelector(selectLoading);
  const error = useAppSelector(selectControlError);
  const [search, setSearch] = useState<string>("");
  const [, setTick] = useState(0);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (currConversationIndex !== null) {
      dispatch(setCurrConversation(null));
    }
    setSearch(e.target.value);
  };

  const filteredConversations = React.useMemo(() => {
    const loweredSearch = search.toLowerCase();

    return conversations.filter((conv) => {
      if (conv.groupName) {
        return (conv.groupName.toLowerCase() + "group").includes(loweredSearch);
      }

      return (conv.fullname?.toLowerCase() ?? "").includes(loweredSearch);
    });
  }, [conversations, search]);

  useEffect(() => {
    const loadConvs = async () => {
      await dispatch(loadConversations());
    };

    loadConvs();
  }, [dispatch]);

  // Refresh elapsed times every minute so they don't go stale
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const renderedConversations = () => {
    // TODO: add message w/same styling as error msg to tell user that no convs were found if no results
    return filteredConversations.map((c: Conversation) => {
      const [username, name] = processFullname(c.fullname, c.groupName);
      let timePreview = c.updated && elapsedTime(c.updated);
      if (timePreview === "0m") {
        timePreview = "now";
      }

      const changeConversation = () => {
        if (
          currConversationIndex != null &&
          conversations[currConversationIndex]?.fullname == c.fullname
        ) {
          return;
        }

        // Find original index of the conversation you just selected
        // We do it this way so we can select correct conversation if original array is filtered
        const originalIndex = conversations.findIndex(
          (conv) => conv.fullname === c.fullname
        );

        dispatch(setCurrConversation(originalIndex));
      };

      // flex-wrap: when the name and timestamp don't fit on one row, the
      // timestamp drops to its own row instead of the name truncating.
      const conversationClass = clsx(
        "active:bg-neutral-300 hover:cursor-pointer flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 p-3 md:p-4 transition rounded-md hover:bg-neutral-200 ease",
        {
          "bg-neutral-200 hover:bg-neutral-200":
            currConversationIndex !== null &&
            conversations[currConversationIndex]?.fullname === c.fullname,
        }
      );

      return (
        <div
          // addressIds are only unique per type — a user DM and a group can share one.
          key={`${c.addressType}:${c.addressId}`}
          className={conversationClass}
          onClick={() => changeConversation()}
        >
          <p className="font-light min-w-0 [overflow-wrap:anywhere]">
            <span className="font-semibold">{username}</span>
            {name}
          </p>
          <p className={clsx("text-xs shrink-0", { "text-blue-500 font-semibold": c.unread })}>
            {c.updated ? timePreview : <PencilLineIcon size={22} />}
          </p>
        </div>
      );
    });
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4">
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-center justify-between">
          <h1 className="text-xl font-semibold">{"Chats"}</h1>
          <NewConversationDialog />
        </div>
        <Input
          type="text"
          placeholder={CONTROL_SEARCH_PLACEHOLDER}
          value={search}
          onChange={handleSearch}
        />
      </div>

      {/* pr-2: keep row timestamps clear of the overlay scrollbar */}
      <div className="flex flex-col flex-grow w-full gap-2 pr-2 overflow-y-auto">
        {error && <p className="text-neutral-400">{CONTROL_LOAD_ERROR_MSG}</p>}
        {!loading && error === "" && renderedConversations()}
      </div>
    </div>
  );
};

export default ConversationList;
